import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getProductListByColumn } from '../../../api/services/productService';
import SliderView from '../../../components/SliderView';
import ProductCard from '../../../components/ProductCard';

const SectionContainer = styled.section`
  margin: 0 auto;
  padding: 20px;
  width: 90%;

  @media (max-width: 768px) {
    width: 95%;
    padding: 15px;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 10px;
  }
`;

// Mobile Slider Container
const MobileSlider = styled.div`
  display: none;
  position: relative;
  overflow: hidden;
  width: 100%;
  margin-top: 20px;

  @media (max-width: 768px) {
    display: block;
    width: 100%;
    padding: 0 5px 40px 5px;
  }
`;

const SliderTrack = styled.div`
  display: flex;
  transition: transform 0.3s ease-in-out;
  transform: translateX(${(props) => props.currentSlide * -100}%);
`;

const DotsContainer = styled.div`
  display: none;
  position: absolute;
  width: 100%;
  bottom: 10px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  z-index: 10;

  @media (max-width: 768px) {
    display: flex;
    bottom: 15px;
    gap: 6px;
  }
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #ccc;
  cursor: pointer;
  transition: background-color 0.3s ease;

  ${(props) =>
    props.active &&
    `
      background-color: #000;
      transform: scale(1.2);
    `}

  @media (max-width: 768px) {
    width: 6px;
    height: 6px;
  }
`;

const DesktopSliderView = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileTitle = styled.h2`
  display: none;
  font-size: 1.8rem;
  font-weight: 600;
  margin-left: 30px;
  margin-bottom: 20px;
  text-align: left;
  color: ${(props) => props.theme.colors.primaryText};

  @media (max-width: 768px) {
    display: block;
    font-size: 1.5rem;
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 10px;
  }
`;

export default function NewArrivalsView() {
  const userInfo = useSelector((state) => state.userInfo);
  const [newArrivalsProductList, setNewArrivalsProductList] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    getNewArrivalsProductList();
  }, []);

  const getNewArrivalsProductList = async () => {
    try {
      const newArrivalsResult = await getProductListByColumn(
        'newarrival',
        userInfo.idCustomer,
        userInfo.brandId,
        '01', // Always use price level 01
      );

      // Take first 8 products for new arrivals display
      const newArrivalsProducts =
        newArrivalsResult.productList?.slice(0, 8) || [];

      setNewArrivalsProductList(newArrivalsProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) =>
      prev === Math.ceil(newArrivalsProductList.length / 2) - 1
        ? prev
        : prev + 1,
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? prev : prev - 1));
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Touch handlers for swipe functionality
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };

  return (
    <SectionContainer>
      {newArrivalsProductList.length > 0 ? (
        <>
          {/* Desktop/Tablet Slider */}
          <DesktopSliderView>
            <SliderView title={'New Arrivals'}>
              {newArrivalsProductList.map((item, index) => (
                <div key={`product_new_arrivals_${index}`}>
                  <ProductCard data={item} key={`product_${index}`} />
                </div>
              ))}
            </SliderView>
          </DesktopSliderView>

          {/* Mobile Slider */}
          <MobileTitle>New Arrivals</MobileTitle>
          <MobileSlider
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <SliderTrack currentSlide={currentSlide}>
              {newArrivalsProductList.map((item, index) => (
                <div
                  key={`product_new_arrivals_${index}`}
                  style={{
                    width: '50%',
                    flexShrink: 0,
                    padding: '0 5px',
                  }}
                >
                  <ProductCard data={item} key={`product_${index}`} />
                </div>
              ))}
            </SliderTrack>
            <DotsContainer>
              {Array.from({
                length: Math.ceil(newArrivalsProductList.length / 2),
              }).map((_, idx) => (
                <Dot
                  key={idx}
                  active={idx === currentSlide}
                  onClick={() => goToSlide(idx)}
                />
              ))}
            </DotsContainer>
          </MobileSlider>
        </>
      ) : null}
    </SectionContainer>
  );
}
